/**
 * Tool Presenter Map configuring human-readable running and completed action descriptions,
 * parameter interpolation keys, hidden plumbing tools, and chained/merged tools.
 */
export const TOOL_PRESENTER_MAP = {
  query_research: {
    runningKey: "assistant.tools.query_research.running",
    doneKey: "assistant.tools.query_research.done",
    defaultRunning: 'Researching "{{query}}" across AI engines...',
    defaultDone: 'Researched "{{query}}"',
    paramKey: "query",
  },
  get_task_result: {
    mergeInto: "query_research",
    runningKey: "assistant.tools.get_task_result.running",
    doneKey: "assistant.tools.get_task_result.done",
    defaultRunning: "Waiting for research results...",
    defaultDone: "Research complete",
  },
  whoami: {
    hidden: true,
  },
  extract_page_content: {
    runningKey: "assistant.tools.extract_page_content.running",
    doneKey: "assistant.tools.extract_page_content.done",
    defaultRunning: "Extracting webpage content...",
    defaultDone: "Extracted webpage content",
    paramKey: "url",
  },
  genai_queries: {
    runningKey: "assistant.tools.genai_queries.running",
    doneKey: "assistant.tools.genai_queries.done",
    defaultRunning: "Extracting GenAI target queries...",
    defaultDone: "Extracted GenAI target queries",
  },
  closest_url: {
    runningKey: "assistant.tools.closest_url.running",
    doneKey: "assistant.tools.closest_url.done",
    defaultRunning: "Finding matching landing page...",
    defaultDone: "Matched landing page",
  },
  related_queries: {
    runningKey: "assistant.tools.related_queries.running",
    doneKey: "assistant.tools.related_queries.done",
    defaultRunning: "Identifying related queries...",
    defaultDone: "Identified related queries",
  },
  generate_content_brief_async: {
    runningKey: "assistant.tools.generate_content_brief_async.running",
    doneKey: "assistant.tools.generate_content_brief_async.done",
    defaultRunning: "Generating optimization content brief...",
    defaultDone: "Generated content brief",
  },
  generate_content_brief: {
    runningKey: "assistant.tools.generate_content_brief_async.running",
    doneKey: "assistant.tools.generate_content_brief_async.done",
    defaultRunning: "Generating optimization content brief...",
    defaultDone: "Generated content brief",
  },
  generate_content_draft_async: {
    runningKey: "assistant.tools.generate_content_draft_async.running",
    doneKey: "assistant.tools.generate_content_draft_async.done",
    defaultRunning: "Drafting webpage content...",
    defaultDone: "Drafted webpage content",
  },
  generate_content_draft: {
    runningKey: "assistant.tools.generate_content_draft_async.running",
    doneKey: "assistant.tools.generate_content_draft_async.done",
    defaultRunning: "Drafting webpage content...",
    defaultDone: "Drafted webpage content",
  },
  regenerate_content_brief_with_feedback_async: {
    runningKey: "assistant.tools.regenerate_content_brief_with_feedback_async.running",
    doneKey: "assistant.tools.regenerate_content_brief_with_feedback_async.done",
    defaultRunning: "Regenerating content brief with feedback...",
    defaultDone: "Regenerated content brief",
  },
  generate_visual_analytics: {
    runningKey: "assistant.tools.generate_visual_analytics.running",
    doneKey: "assistant.tools.generate_visual_analytics.done",
    errorKey: "assistant.tools.generate_visual_analytics.error",
    defaultRunning: "Building chart visualization...",
    defaultDone: "Built chart",
    defaultError: "Chart attempt failed, retrying",
  },
  get_score: {
    runningKey: "assistant.tools.get_score.running",
    doneKey: "assistant.tools.get_score.done",
    defaultRunning: "Calculating retrieval score...",
    defaultDone: "Calculated retrieval score",
  },
  get_batch_scores: {
    runningKey: "assistant.tools.get_batch_scores.running",
    doneKey: "assistant.tools.get_batch_scores.done",
    defaultRunning: "Calculating batch retrieval scores...",
    defaultDone: "Calculated batch retrieval scores",
  },
  get_trending_citations: {
    runningKey: "assistant.tools.get_trending_citations.running",
    doneKey: "assistant.tools.get_trending_citations.done",
    defaultRunning: "Analyzing citation trends...",
    defaultDone: "Analyzed citation trends",
  },
  export_file: {
    runningKey: "assistant.tools.export_file.running",
    doneKey: "assistant.tools.export_file.done",
    defaultRunning: "Exporting file: {{file_name}}...",
    defaultDone: "Exported file: {{file_name}}",
    paramKey: "file_name",
  },
  export_report_file: {
    runningKey: "assistant.tools.export_file.running",
    doneKey: "assistant.tools.export_file.done",
    defaultRunning: "Exporting report: {{file_name}}...",
    defaultDone: "Exported report: {{file_name}}",
    paramKey: "file_name",
  },
  send_report: {
    runningKey: "assistant.tools.send_report.running",
    doneKey: "assistant.tools.send_report.done",
    defaultRunning: "Sending performance report email...",
    defaultDone: "Sent performance report email",
  },
  record_memory: {
    runningKey: "assistant.tools.record_memory.running",
    doneKey: "assistant.tools.record_memory.done",
    defaultRunning: "Updating memory context...",
    defaultDone: "Updated memory context",
  },
  geo_dashboard_citation_overview: {
    runningKey: "assistant.tools.citation_overview.running",
    doneKey: "assistant.tools.citation_overview.done",
    defaultRunning: "Querying citation share...",
    defaultDone: "Queried citation share",
  },
  citation_overview: {
    runningKey: "assistant.tools.citation_overview.running",
    doneKey: "assistant.tools.citation_overview.done",
    defaultRunning: "Querying citation share...",
    defaultDone: "Queried citation share",
  },
  geo_dashboard_top_cited_pages: {
    runningKey: "assistant.tools.top_cited_pages.running",
    doneKey: "assistant.tools.top_cited_pages.done",
    defaultRunning: "Fetching top cited pages...",
    defaultDone: "Fetched top cited pages",
  },
  top_cited_pages: {
    runningKey: "assistant.tools.top_cited_pages.running",
    doneKey: "assistant.tools.top_cited_pages.done",
    defaultRunning: "Fetching top cited pages...",
    defaultDone: "Fetched top cited pages",
  },
  geo_dashboard_top_topics: {
    runningKey: "assistant.tools.top_topics.running",
    doneKey: "assistant.tools.top_topics.done",
    defaultRunning: "Fetching citation topics...",
    defaultDone: "Fetched citation topics",
  },
  top_topics: {
    runningKey: "assistant.tools.top_topics.running",
    doneKey: "assistant.tools.top_topics.done",
    defaultRunning: "Fetching citation topics...",
    defaultDone: "Fetched citation topics",
  },
  geo_dashboard_mention_overview: {
    runningKey: "assistant.tools.mention_overview.running",
    doneKey: "assistant.tools.mention_overview.done",
    defaultRunning: "Querying brand mentions...",
    defaultDone: "Queried brand mentions",
  },
  mention_overview: {
    runningKey: "assistant.tools.mention_overview.running",
    doneKey: "assistant.tools.mention_overview.done",
    defaultRunning: "Querying brand mentions...",
    defaultDone: "Queried brand mentions",
  },
  geo_dashboard_my_top_mentions: {
    runningKey: "assistant.tools.my_top_mentions.running",
    doneKey: "assistant.tools.my_top_mentions.done",
    defaultRunning: "Fetching top mentions...",
    defaultDone: "Fetched top mentions",
  },
  my_top_mentions: {
    runningKey: "assistant.tools.my_top_mentions.running",
    doneKey: "assistant.tools.my_top_mentions.done",
    defaultRunning: "Fetching top mentions...",
    defaultDone: "Fetched top mentions",
  },
  geo_dashboard_citation_trend: {
    runningKey: "assistant.tools.citation_trend.running",
    doneKey: "assistant.tools.citation_trend.done",
    defaultRunning: "Analyzing citation trends...",
    defaultDone: "Analyzed citation trends",
  },
  citation_trend: {
    runningKey: "assistant.tools.citation_trend.running",
    doneKey: "assistant.tools.citation_trend.done",
    defaultRunning: "Analyzing citation trends...",
    defaultDone: "Analyzed citation trends",
  },
  geo_dashboard_mention_trend: {
    runningKey: "assistant.tools.mention_trend.running",
    doneKey: "assistant.tools.mention_trend.done",
    defaultRunning: "Analyzing mention trends...",
    defaultDone: "Analyzed mention trends",
  },
  mention_trend: {
    runningKey: "assistant.tools.mention_trend.running",
    doneKey: "assistant.tools.mention_trend.done",
    defaultRunning: "Analyzing mention trends...",
    defaultDone: "Analyzed mention trends",
  },
  add_query: {
    runningKey: "assistant.tools.add_query.running",
    doneKey: "assistant.tools.add_query.done",
    defaultRunning: 'Tracking query "{{query}}"...',
    defaultDone: 'Tracked query "{{query}}"',
    paramKey: "query",
  },
  add_url: {
    runningKey: "assistant.tools.add_url.running",
    doneKey: "assistant.tools.add_url.done",
    defaultRunning: "Tracking URL...",
    defaultDone: "Tracked URL",
  },
  studio: {
    runningKey: "assistant.tools.studio.running",
    doneKey: "assistant.tools.studio.done",
    defaultRunning: "Querying GEO Studio intelligence...",
    defaultDone: "Queried GEO Studio intelligence",
  },
  studio_queries: {
    runningKey: "assistant.tools.studio_queries.running",
    doneKey: "assistant.tools.studio_queries.done",
    defaultRunning: "Querying GEO Studio query evidence...",
    defaultDone: "Queried GEO Studio query evidence",
  },
};

/**
 * Returns true if a tool should be hidden from the user-facing timeline (e.g. whoami).
 */
export const isToolHidden = (toolName) => {
  return Boolean(TOOL_PRESENTER_MAP[toolName]?.hidden);
};

/**
 * Returns the target tool name if this tool merges into another action (e.g. get_task_result -> query_research).
 */
export const getToolMergeTarget = (toolName) => {
  return TOOL_PRESENTER_MAP[toolName]?.mergeInto || null;
};

/**
 * Format raw tool name and execution arguments into a human-readable present or past-tense label.
 *
 * @param {Object} step - Reasoning step object containing tool_name and tool_args
 * @param {Function} [t] - Optional i18next translation function
 * @param {boolean} [isRunning=false] - Whether the tool is currently running
 * @returns {string} Human-readable tool label
 */
export const formatToolDisplayName = (step, t, isRunning = false) => {
  if (!step || typeof step !== "object") return "";

  const toolName = step.tool_name || step.name || "";
  if (!toolName) return `Step ${step.step || 1}`;

  let parsedArgs = {};
  if (step.tool_args) {
    if (typeof step.tool_args === "object" && step.tool_args !== null) {
      parsedArgs = step.tool_args;
    } else if (typeof step.tool_args === "string") {
      try {
        parsedArgs = JSON.parse(step.tool_args);
      } catch (e) {
        parsedArgs = { raw: step.tool_args };
      }
    }
  }

  // Handle nested input structures if present
  if (parsedArgs.input && typeof parsedArgs.input === "object") {
    parsedArgs = { ...parsedArgs, ...parsedArgs.input };
  }

  const presenter = TOOL_PRESENTER_MAP[toolName];
  const isError = step.status === "error";
  const targetKey = isError
    ? presenter?.errorKey || `assistant.tools.${toolName}.error`
    : isRunning
    ? presenter?.runningKey || `assistant.tools.${toolName}.running`
    : presenter?.doneKey || `assistant.tools.${toolName}.done`;

  const defaultString = isError
    ? presenter?.defaultError || (presenter?.defaultDone ? `${presenter.defaultDone} failed` : "Action failed")
    : isRunning
    ? presenter?.defaultRunning
    : presenter?.defaultDone;

  if (typeof t === "function") {
    let interpolatedDefault = defaultString || "";
    if (interpolatedDefault && typeof parsedArgs === "object") {
      Object.keys(parsedArgs).forEach((k) => {
        const val = parsedArgs[k];
        if (typeof val === "string" || typeof val === "number") {
          interpolatedDefault = interpolatedDefault.replace(
            new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g"),
            String(val),
          );
        }
      });
    }

    const translated = t(targetKey, {
      ...parsedArgs,
      defaultValue: interpolatedDefault,
    });
    if (translated && translated !== targetKey) {
      return translated;
    }
    if (interpolatedDefault) {
      return interpolatedDefault;
    }
  } else if (defaultString) {
    let str = defaultString;
    if (typeof parsedArgs === "object") {
      Object.keys(parsedArgs).forEach((k) => {
        const val = parsedArgs[k];
        if (typeof val === "string" || typeof val === "number") {
          str = str.replace(
            new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g"),
            String(val),
          );
        }
      });
    }
    return str;
  }

  // Default fallback: Title Case from snake_case
  return toolName
    .replace(/^geo_dashboard_/, "")
    .replace(/_async$/, "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};
